/**
 * Cristaux cart sync (cart drawer): production setup fee + color fill
 * ---------------------------------------------------------------------------
 * Setup fee rule (same as the Cristaux app, scripts.js:928–1017):
 *   - Subtotal of non-custom items (original prices) under $500 → fee qty 1
 *   - $500 or more, or no regular items in the cart          → fee qty 0
 *   - B2B customers (isB2B) are never charged the fee
 *
 * Color fill rule (same as the app, scripts.js:282–424):
 *   - Each COLORFILL line is matched to its product by SKU + color
 *     (COLORFILL properties "Product Sku" + "Color" ↔ product SKU + property "ColorFill")
 *   - Its quantity follows the product's quantity; orphaned color fills are removed
 *   - Unlike the app, quantities of product lines sharing SKU + color are summed
 *
 * Why this exists: the app only runs these rules on /cart. The new theme uses
 * the cart drawer, so this script applies the same rules there.
 *
 * How it stays safe:
 *   - Idempotent: reads /cart.js, compares what it wants with what is there, and
 *     only changes the cart when they differ. Repeated events are no-ops.
 *   - Never publishes cartUpdate itself, so it cannot trigger itself in a loop.
 *   - One run at a time; events arriving mid-run queue a single re-check.
 *   - Skips /cart, where the app still handles both rules, to avoid double edits.
 *
 * window.CristauxCartSync.run() returns a promise that settles once the cart is
 * reconciled; the drawer checkout (assets/cristaux-drawer-checkout.js) awaits it.
 *
 * The "$X away" message is rendered server-side by snippets/cristaux-fee-message.liquid,
 * so it is always correct after any drawer re-render.
 *
 * Config: window.CristauxFeeConfig (snippets/cristaux-app.liquid).
 */
(() => {
  const cfg = window.CristauxFeeConfig;
  if (!cfg || cfg.isCartPage) return;
  const chargeFee = !(typeof isB2B !== 'undefined' && isB2B);

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const excluded = new Set(cfg.excludedVariantIds.map(String));
  const feeId = String(cfg.feeVariantId);
  const COLOR_FILL_SKU = 'COLORFILL';

  let running = null;
  let rerun = false;

  const fetchCart = () =>
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const wantedFeeQty = (cart) => {
    let total = 0;
    let count = 0;
    cart.items.forEach((item) => {
      if (excluded.has(String(item.variant_id))) return;
      total += item.original_line_price;
      count += 1;
    });
    return count > 0 && total < cfg.thresholdCents ? 1 : 0;
  };

  const currentFeeQty = (cart) =>
    cart.items
      .filter((item) => String(item.variant_id) === feeId)
      .reduce((sum, item) => sum + item.quantity, 0);

  // [{ key, quantity }] for every color fill line whose quantity is wrong.
  const colorFillChanges = (cart) => {
    const productQty = {};
    cart.items.forEach((item) => {
      if (item.sku === COLOR_FILL_SKU) return;
      const key = `${item.sku}__${(item.properties || {}).ColorFill}`;
      productQty[key] = (productQty[key] || 0) + item.quantity;
    });
    return cart.items
      .filter((item) => item.sku === COLOR_FILL_SKU)
      .map((item) => {
        const props = item.properties || {};
        return { key: item.key, quantity: productQty[`${props['Product Sku']}__${props.Color}`] || 0, have: item.quantity };
      })
      .filter((change) => change.quantity !== change.have);
  };

  const sectionsToRender = () => {
    const ids = [];
    if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    return ids;
  };

  const post = async (path, payload) => {
    payload.sections = sectionsToRender();
    payload.sections_url = window.location.pathname;
    const response = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const result = await response.json();
    if (result && result.status && result.status >= 400) {
      console.warn('[Cristaux cart sync] cart update rejected:', result.description || result.message);
      return null;
    }
    return result;
  };

  const applyFee = (qty, alreadyInCart) =>
    alreadyInCart
      ? post('cart/change.js', { id: feeId, quantity: qty })
      : post('cart/add.js', { items: [{ id: Number(feeId), quantity: qty }] });

  const renderSections = (sections) => {
    if (!sections) return;
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

    if (sections['cart-drawer']) {
      const current = document.querySelector('#CartDrawer .drawer__inner');
      const fresh = parse(sections['cart-drawer']).querySelector('.drawer__inner');
      if (current && fresh) current.innerHTML = fresh.innerHTML;
    }
    if (sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      const fresh = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
      if (bubble && fresh) bubble.innerHTML = fresh.innerHTML;
    }
  };

  const reconcile = async () => {
    let lastSections = null;
    let changed = false;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      rerun = false;
      let cart = await fetchCart();

      // Color fill first: line keys stay valid across sequential changes.
      const fills = colorFillChanges(cart);
      for (const fill of fills) {
        const result = await post('cart/change.js', { id: fill.key, quantity: fill.quantity });
        if (result) {
          lastSections = result.sections || lastSections;
          changed = true;
        }
      }
      if (fills.length) cart = await fetchCart();

      if (chargeFee) {
        const want = wantedFeeQty(cart);
        const have = currentFeeQty(cart);
        if (want !== have) {
          const result = await applyFee(want, have > 0);
          if (result) {
            lastSections = result.sections || lastSections;
            changed = true;
          }
        }
      }
      if (!rerun) break;
    }

    if (changed) {
      renderSections(lastSections);
      const after = await fetchCart();
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', after.item_count === 0);
    }
  };

  const sync = () => {
    if (running) {
      rerun = true;
      return running;
    }
    running = reconcile()
      .catch((error) => console.error('[Cristaux cart sync] sync failed:', error))
      .finally(() => {
        running = null;
      });
    return running;
  };

  window.CristauxCartSync = { run: sync };

  const init = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      // Fires after add-to-cart (product form) and after quantity change / remove in the drawer.
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(sync, 0));
    } else {
      console.warn('[Cristaux cart sync] Dawn pub/sub not found; cart only checked on page load.');
    }
    sync(); // Reconcile on page load (cart may have changed in another tab or session).
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
